// Question flags / "Report an issue" (PIX-711). Mirrors the backend reason
// map in students/flags.py - keep the two in sync.

export const FLAG_TAB = Object.freeze({
  QUESTION: 'question',
  MARK_SCHEME: 'mark_scheme',
  SORTED_SOLUTION: 'sorted_solution',
  YOUR_MARKS: 'your_marks',
});

export const FLAG_ENTRY_POINT = Object.freeze({
  FLAG_BUTTON: 'flag_button',
  REPORT_ICON: 'report_icon',
});

export const FLAG_SOURCE = Object.freeze({
  TOPIC_WISE: 'topic_wise',
  RECOMMENDED: 'recommended',
  PAST_PAPERS: 'past_papers',
  SAVED: 'saved',
  EXAM_BUILDER: 'exam_builder',
  WORKSHEET: 'worksheet',
});

export const OTHER_REASON = 'OTHER';
export const COMMENT_MAX_LENGTH = 500;

const OTHER = { value: OTHER_REASON, label: 'Other' };
const FORMATTING = { value: 'FORMATTING_TYPO', label: 'Formatting or typo' };
const SORTY_GRADING = {
  value: 'SORTY_GRADING',
  label: 'Incorrect grading by sorty marks',
};

export const FLAG_REASONS = Object.freeze({
  [FLAG_TAB.QUESTION]: [
    { value: 'BUTTONS_NOT_WORKING', label: 'Buttons not working' },
    {
      value: 'QUESTION_INCOMPLETE',
      label: 'Question is incomplete / incorrect',
    },
    SORTY_GRADING,
    FORMATTING,
    OTHER,
  ],
  [FLAG_TAB.MARK_SCHEME]: [
    { value: 'INCORRECT_MARK_SCHEME', label: 'Incorrect mark scheme' },
    { value: 'MISMATCH_QUESTION', label: "Doesn't match the question" },
    FORMATTING,
    OTHER,
  ],
  [FLAG_TAB.SORTED_SOLUTION]: [
    { value: 'INCORRECT_STEPS', label: 'Incorrect steps' },
    { value: 'MISSING_STEPS', label: 'Missing steps' },
    { value: 'WRONG_ANSWER', label: 'Wrong answer' },
    { value: 'UNCLEAR', label: 'Unclear explanation' },
    OTHER,
  ],
  [FLAG_TAB.YOUR_MARKS]: [
    SORTY_GRADING,
    { value: 'MARKS_MISMATCH', label: "Marks don't match the mark scheme" },
    FORMATTING,
    OTHER,
  ],
});

const REPORT_SUBTITLES = Object.freeze({
  [FLAG_TAB.QUESTION]: 'Something wrong with this question? Let us know.',
  [FLAG_TAB.MARK_SCHEME]: 'Something off with this mark scheme? Let us know.',
  [FLAG_TAB.SORTED_SOLUTION]:
    'Something wrong with this solution? Let us know.',
  [FLAG_TAB.YOUR_MARKS]: 'Something off with your sorty marks? Let us know.',
});

export function flagModalCopy(tab, entryPoint) {
  if (entryPoint === FLAG_ENTRY_POINT.FLAG_BUTTON) {
    return {
      title: 'Flag Solution',
      subtitle:
        "Let us know what's wrong with this solution so we can improve it.",
      removeLabel: 'Remove flag',
      removedToast: 'Flag removed',
    };
  }
  return {
    title: 'Report an issue',
    subtitle: REPORT_SUBTITLES[tab] ?? REPORT_SUBTITLES[FLAG_TAB.QUESTION],
    removeLabel: 'Remove report',
    removedToast: 'Report removed',
  };
}

export const SAVED_TOAST = 'Thanks - your feedback helps us improve.';

// Question-screen tab keys shared by the past-paper, mock-exam and worksheet
// views: '1' Question, '2' Mark Scheme, '3' Sorted Solution, 'your-marks'.
const FLAG_TAB_BY_TAB_KEY = Object.freeze({
  1: FLAG_TAB.QUESTION,
  2: FLAG_TAB.MARK_SCHEME,
  3: FLAG_TAB.SORTED_SOLUTION,
  'your-marks': FLAG_TAB.YOUR_MARKS,
});

export function flagTabForTabKey(tabKey) {
  return FLAG_TAB_BY_TAB_KEY[tabKey] ?? FLAG_TAB.QUESTION;
}

const SOURCE_BY_VIEW_CATEGORY = Object.freeze({
  'topic-wise': FLAG_SOURCE.TOPIC_WISE,
  recommended: FLAG_SOURCE.RECOMMENDED,
  'past-papers': FLAG_SOURCE.PAST_PAPERS,
  'exam-builder': FLAG_SOURCE.EXAM_BUILDER,
  'my-worksheets': FLAG_SOURCE.WORKSHEET,
});

const SOURCE_BY_ROUTE_NAME = Object.freeze({
  'past-papers-question-detail': FLAG_SOURCE.PAST_PAPERS,
  'exam-builder-question-detail': FLAG_SOURCE.EXAM_BUILDER,
  'student-worksheet-question-detail': FLAG_SOURCE.WORKSHEET,
});

/** Which screen the student reported from, for the team's triage. */
export function flagSourceFromRoute(route) {
  if (route?.query?.from === 'saved-questions') return FLAG_SOURCE.SAVED;
  return (
    SOURCE_BY_VIEW_CATEGORY[route?.params?.viewCategory] ??
    SOURCE_BY_ROUTE_NAME[route?.name] ??
    null
  );
}

/** A prefilled flag with a comment on a non-Other reason predates the
 * "details only for Other" rule; keep showing it so it isn't silently lost. */
export function hasLegacyComment(flag) {
  return Boolean(flag?.comment) && flag?.reason !== OTHER_REASON;
}

export function showsDetails(reason, legacyComment) {
  return reason === OTHER_REASON || legacyComment;
}

export function canSubmitFlag({ reason, comment }) {
  if (!reason) return false;
  if (reason === OTHER_REASON) return Boolean(comment?.trim());
  return true;
}

export function buildFlagPayload({
  tab,
  reason,
  comment,
  source,
  worksheetUuid,
  entryPoint,
  legacyComment = false,
}) {
  const payload = {
    tab,
    reason,
    comment: showsDetails(reason, legacyComment)
      ? (comment ?? '').trim().slice(0, COMMENT_MAX_LENGTH)
      : '',
    entry_point: entryPoint,
  };
  if (source) payload.source = source;
  if (source === FLAG_SOURCE.WORKSHEET && worksheetUuid) {
    payload.worksheet = worksheetUuid;
  }
  return payload;
}

export function flagEndpoint(questionKind, questionUuid) {
  return questionKind === 'mock_exam'
    ? `api/mock-exam-questions/${questionUuid}/flag-solution/`
    : `students/api/questions/${questionUuid}/flag-solution/`;
}
